import { Component, OnInit, PLATFORM_ID, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { StorageService } from '../../../services/storage.service';
import { TagManagerService } from '../../../services/tag-manager/tag-manager.service';
import { ConsentUiService } from '../../../services/consent-ui.service';

@Component({
  selector: 'app-cookie-consent',
  standalone: true,
  imports: [],
  templateUrl: './cookie-consent.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cookie-consent.component.scss'
})
export class CookieConsentComponent implements OnInit {
  private storage = inject(StorageService);
  private tagManager = inject(TagManagerService);
  private consentUi = inject(ConsentUiService);
  private platformId = inject(PLATFORM_ID);

  /** No decision recorded yet — the first-visit case. */
  private readonly undecided = signal(false);

  /** The stored decision, so a returning visitor sees what they picked. */
  readonly currentChoice = signal<string | null>(null);

  /**
   * R3-23: shown on first visit, and whenever the visitor re-opens it from the
   * footer. Re-opening is what makes withdrawal possible at all.
   */
  readonly showBanner = computed(() => this.undecided() || this.consentUi.reopenRequested());

  /** True only when reached deliberately, so the banner can offer a Close. */
  readonly isRevisiting = computed(() => this.consentUi.reopenRequested());

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const consent = this.storage.get('trackingConsentAccepted');
      this.currentChoice.set(consent);
      this.undecided.set(consent === null);
    }
  }

  acceptAll() {
    this.tagManager.updateConsent(true);
    this.currentChoice.set('true');
    this.dismiss();
  }

  declineAll() {
    const hadAccepted = this.currentChoice() === 'true';
    this.tagManager.updateConsent(false);
    this.currentChoice.set('false');
    this.dismiss();

    // WHY: suppressTagsForPayment() removes the script nodes and nulls the
    // globals, but it cannot unload GTM/Bing/FB code that has already executed
    // (the standing NEW-01/SEC-03 limitation). If the visitor had previously
    // accepted, those trackers ARE running, so honouring the withdrawal means
    // reloading into a state where they were never injected. Only on an actual
    // accepted -> declined transition; a first-time decline has nothing loaded.
    if (hadAccepted && isPlatformBrowser(this.platformId)) {
      window.location.reload();
    }
  }

  /** Close without changing the stored decision. */
  close() {
    this.dismiss();
  }

  private dismiss() {
    this.undecided.set(false);
    this.consentUi.close();
  }
}
